import { useState, useEffect, useCallback } from 'react';

// Split typewriter - static prefix with changing suffix
export function useSplitTypewriter(prefix, suffixes, speed = 50, pauseDuration = 1800) {
  const [currentSuffixIndex, setCurrentSuffixIndex] = useState(0);
  const [displaySuffix, setDisplaySuffix] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [phase, setPhase] = useState('typing'); // 'typing', 'pausing', 'deleting'

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setDisplaySuffix(suffixes[0]);
      return;
    }

    let timeoutId;
    const currentSuffix = suffixes[currentSuffixIndex];

    const animate = () => {
      if (phase === 'typing') {
        if (displaySuffix.length < currentSuffix.length) {
          setDisplaySuffix(currentSuffix.slice(0, displaySuffix.length + 1));
          setIsTyping(true);
          timeoutId = setTimeout(animate, speed);
        } else {
          setIsTyping(false);
          setPhase('pausing');
          timeoutId = setTimeout(animate, pauseDuration);
        }
      } else if (phase === 'pausing') {
        setPhase('deleting');
        timeoutId = setTimeout(animate, speed * 0.5);
      } else if (phase === 'deleting') {
        if (displaySuffix.length > 0) {
          setDisplaySuffix(displaySuffix.slice(0, -1));
          setIsTyping(true);
          timeoutId = setTimeout(animate, speed * 0.4);
        } else {
          setIsTyping(false);
          setCurrentSuffixIndex((prev) => (prev + 1) % suffixes.length);
          setPhase('typing');
          timeoutId = setTimeout(animate, 300);
        }
      }
    };

    timeoutId = setTimeout(animate, 150);

    return () => clearTimeout(timeoutId);
  }, [suffixes, currentSuffixIndex, displaySuffix, phase, speed, pauseDuration]);

  return { prefix, displaySuffix, isTyping, currentSuffixIndex };
}

export function useTypewriter(text, speed = 50, delay = 0, loop = false) {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setDisplayText(text);
      setIsComplete(true);
      return;
    }

    let timeoutId;
    let currentIndex = 0;
    let deleting = false;
    
    const startTimeout = setTimeout(() => {
      setIsTyping(true);
      
      const type = () => {
        if (!deleting) {
          if (currentIndex < text.length) {
            setDisplayText(text.slice(0, currentIndex + 1));
            currentIndex++;
            timeoutId = setTimeout(type, speed);
          } else {
            setIsTyping(false);
            setIsComplete(true);
            
            if (loop) {
              timeoutId = setTimeout(() => {
                deleting = true;
                type();
              }, 2500);
            }
          }
        } else {
          if (currentIndex > 0) {
            currentIndex--;
            setDisplayText(text.slice(0, currentIndex));
            timeoutId = setTimeout(type, speed / 2);
          } else {
            deleting = false;
            setIsComplete(false);
            timeoutId = setTimeout(type, 600);
          }
        }
      };
      
      type();
    }, delay);

    return () => {
      clearTimeout(startTimeout);
      clearTimeout(timeoutId);
    };
  }, [text, speed, delay, loop]);

  return { displayText, isTyping, isComplete };
}

// Multi-text typewriter that cycles through texts
export function useMultiTypewriter(texts, speed = 50, pauseDuration = 2000) {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setDisplayText(texts[0]);
      return;
    }

    let timeoutId;
    const currentText = texts[currentTextIndex];

    const animate = () => {
      if (phase === 'typing') {
        if (displayText.length < currentText.length) {
          setDisplayText(currentText.slice(0, displayText.length + 1));
          setIsTyping(true);
          timeoutId = setTimeout(animate, speed);
        } else {
          setIsTyping(false);
          setPhase('pausing');
          timeoutId = setTimeout(animate, pauseDuration);
        }
      } else if (phase === 'pausing') {
        setPhase('deleting');
        timeoutId = setTimeout(animate, speed / 2);
      } else if (phase === 'deleting') {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
          setIsTyping(true);
          timeoutId = setTimeout(animate, speed / 2);
        } else {
          setIsTyping(false);
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
          setPhase('typing');
          timeoutId = setTimeout(animate, 400);
        }
      }
    };

    timeoutId = setTimeout(animate, 150);

    return () => clearTimeout(timeoutId);
  }, [texts, currentTextIndex, displayText, phase, speed, pauseDuration]);

  return { displayText, isTyping, currentTextIndex };
}

// Counter animation hook
export function useCountUp(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(!startOnView);

  const startCounting = useCallback(() => {
    setHasStarted(true);
  }, []);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, startCounting };
}
